import { useEffect, useState } from 'react';
import { Alert, Button, Input, Progress, Radio, Select, Space, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/api-service';

// Sunucunun kabul ettiği uzantılar (bkz. NonDicomToDicom.SupportedExtensions). Gerçek tür yine
// sunucuda içerikten belirlenir; buradaki liste yalnızca dosya seçiciyi daraltır.
const ACCEPTED = '.jpg,.jpeg,.png,.bmp,.tif,.tiff,.gif,.webp,.pdf,.dcm';

// RIS modality tablosunda bulunmayan ama bu ekranda anlamlı olan kodlar: OT (Other) ve SC
// (Secondary Capture) dışarıdan eklenen görüntüler için, DOC gömülü PDF için standart karşılıktır.
const EXTRA_MODALITIES = ['OT', 'SC', 'DOC'];

export default function StudyAddDicomModal({ studyRow, onSaved, onCancel }) {
    const { t } = useTranslation();

    const [files, setFiles] = useState([]);
    const [target, setTarget] = useState('new');
    const [seriesInstanceUid, setSeriesInstanceUid] = useState(null);
    const [seriesDescription, setSeriesDescription] = useState('');
    const [modality, setModality] = useState(studyRow.modality || 'OT');
    const [description, setDescription] = useState('');

    const [series, setSeries] = useState([]);
    const [modalities, setModalities] = useState([]);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const [seriesRes, modalityRes] = await Promise.all([
                ApiService.Dicom.GetSeriesMetadata(studyRow.id, { silent: true }),
                ApiService.Modality.GetList(),
            ]);
            if (seriesRes.success) setSeries(seriesRes.data || []);
            if (modalityRes.success) setModalities((modalityRes.data || []).map(x => x.name));
        })();
    }, [studyRow.id]);

    const hasPdf = files.some(file => file.name?.toLowerCase().endsWith('.pdf'));

    const modalityOptions = [...new Set([
        ...(studyRow.modality ? [studyRow.modality] : []),
        ...modalities,
        ...EXTRA_MODALITIES,
    ])].map(name => ({ value: name, label: name }));

    const canSubmit = files.length > 0 && (target === 'new' || !!seriesInstanceUid);

    const submit = async () => {
        setLoading(true);
        setProgress(0);
        try {
            const res = await ApiService.Dicom.AddDicomToStudy(
                studyRow.id,
                files,
                {
                    seriesInstanceUid: target === 'existing' ? seriesInstanceUid : null,
                    seriesDescription: target === 'new' ? seriesDescription?.trim() : null,
                    modality: target === 'new' ? modality : null,
                    description: description?.trim(),
                },
                setProgress
            );

            if (res.success) {
                onSaved && onSaved(res.data);
            }
        } catch {
            // hata mesajını uploadRequest gösterdi
        } finally {
            setLoading(false);
            setProgress(null);
        }
    };

    return (
        <div style={{ padding: '8px 0' }}>
            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('ADD_DICOM_WARNING')}
            />

            <Upload.Dragger
                multiple
                accept={ACCEPTED}
                fileList={files.map((file, index) => ({ uid: `${index}`, name: file.name, status: 'done' }))}
                beforeUpload={(file) => {
                    // Yükleme tek istekte, kaydet'e basınca yapılır: antd'nin kendi yüklemesi kapalı.
                    setFiles(prev => [...prev, file]);
                    return false;
                }}
                onRemove={(item) => setFiles(prev => prev.filter((_, index) => `${index}` !== item.uid))}
            >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">{t('Click or drag files to this area')}</p>
                <p className="ant-upload-hint">{t('ADD_DICOM_FILE_HINT')}</p>
            </Upload.Dragger>

            <div style={{ marginTop: 16 }}>
                <Typography.Text strong>{t('Target Series')}</Typography.Text>
                <Radio.Group
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    style={{ display: 'block', marginTop: 8 }}
                >
                    <Space direction="vertical">
                        <Radio value={'new'}>{t('New series')}</Radio>
                        <Radio value={'existing'} disabled={series.length === 0}>{t('Existing series')}</Radio>
                    </Space>
                </Radio.Group>
            </div>

            {target === 'existing' && (
                <div style={{ marginTop: 12 }}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder={t('Select series')}
                        value={seriesInstanceUid}
                        onChange={setSeriesInstanceUid}
                        options={series.map(item => ({
                            value: item.seriesInstanceUID,
                            label: `${item.seriesNumber || '-'} — ${item.desc || '-'} (${item.modality}, ${item.instanceCount})`,
                        }))}
                    />
                </div>
            )}

            {target === 'new' && (
                <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 12 }}>
                    <div>
                        <Typography.Text>{t('Series Description')}</Typography.Text>
                        <Input
                            value={seriesDescription}
                            onChange={(e) => setSeriesDescription(e.target.value)}
                            placeholder={'RIS'}
                        />
                    </div>
                    <div>
                        <Typography.Text>{t('Modality')}</Typography.Text>
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            value={modality}
                            onChange={setModality}
                            options={modalityOptions}
                        />
                        {hasPdf && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {t('ADD_DICOM_PDF_MODALITY_HINT')}
                            </Typography.Text>
                        )}
                    </div>
                </Space>
            )}

            <div style={{ marginTop: 12 }}>
                <Typography.Text>{t('Description')}</Typography.Text>
                <Input.TextArea
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>

            {progress !== null && <Progress percent={progress} style={{ marginTop: 12 }} />}

            <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel}>{t('Cancel')}</Button>
                    <Button type={'primary'} loading={loading} disabled={!canSubmit} onClick={submit}>
                        {t('Send to PACS')}
                    </Button>
                </Space>
            </div>
        </div>
    );
}
